/**
 * Geometry for the Daily Brief dock flight ("the panel docks into / out of the
 * header bell").
 *
 * On CLOSE the panel card is mounted, so its resting rect is measured live. On
 * OPEN the card hasn't mounted yet (its entrance is the flight), so we replicate
 * the CSS resting geometry from the always-mounted CONTAINER instead — one
 * computation per transition, no per-frame DOM measuring.
 */

import type { DockRect } from '@/common/motion/chatDockMotion';

// ── Solo placement geometry ──────────────────────────────────────────────────
// In solo chat the Brief docks as the TOP THIRD of .conversation-chat-panel
// (a 2-row grid, rows 1fr/2fr when open), centered in the chat's 680px lane:
//   .conversation-chat-panel > .daily-summary-panel-card.daily-summary-modal
//     { max-width: 680px; margin: 16px auto 8px }   (DailySummaryModal.css)
// KEEP IN SYNC with those rules + --chat-lane-max-width (ChatBox.css).
const SOLO_CARD_LANE_MAX_PX = 680;
const SOLO_CARD_MARGIN_TOP = 16;
const SOLO_CARD_MARGIN_BOTTOM = 8;

// Side-by-side solo layout: when the chat AREA is wide enough, the chat is the
// BASELINE — a fixed 712px column (680px sheet + 16px gutters), centered in the
// space left of notifications — and the notifications sheet matches it (same
// 680px) HUGGING THE RIGHT EDGE with the standard 16px gutter, but SHRINKS FIRST
// as the area narrows (its column is the flexible last one). Gated on the
// CONTAINER (chat-area) width, mirroring the `@container chat-area (min-width:
// 1040px)` rules in MainLayout.css / DailySummaryModal.css (grid
// `minmax(0,1fr) 712px minmax(0,1fr) minmax(0,712px)`; the sheet is right-pinned,
// capped at 680). KEEP IN SYNC.
const SIDE_BY_SIDE_MIN_PX = 1040;
const SIDE_COL_PX = 712;
const SIDE_MARGIN = 16;

/**
 * Resting rect of the solo Daily Brief / notifications card. Used as the OPEN
 * flight's landing target before the card mounts. On wide screens it's the
 * full-height sheet hugging the RIGHT edge (16px gutter), sized to the chat's
 * 680px baseline but shrinking first as the area narrows; otherwise the top-third
 * row centered in the chat lane.
 */
export function computeBriefPanelRect(containerRect: DockRect): DockRect {
  // Gate on the chat-area (container) width, matching the @container CSS — the
  // panel fills the chat panel, which is the container's width.
  if (containerRect.width >= SIDE_BY_SIDE_MIN_PX) {
    // Chat holds its fixed 712px column; the notifications column takes what's
    // left, up to 712px (so it shrinks FIRST). Notifications is the last column,
    // so its sheet HUGS THE RIGHT EDGE with a 16px gutter, capped at the chat's
    // 680px (= notifCol − 16px gutter once the column reaches 696px).
    const notifCol = Math.min(SIDE_COL_PX, containerRect.width - SIDE_COL_PX);
    const width = Math.max(0, Math.min(SOLO_CARD_LANE_MAX_PX, notifCol - SIDE_MARGIN));
    return {
      top: containerRect.top + SIDE_MARGIN,
      left: containerRect.left + containerRect.width - SIDE_MARGIN - width,
      width,
      height: Math.max(0, containerRect.height - SIDE_MARGIN * 2),
    };
  }
  const rowHeight = containerRect.height / 3;
  const width = Math.max(0, Math.min(SOLO_CARD_LANE_MAX_PX, containerRect.width));
  return {
    top: containerRect.top + SOLO_CARD_MARGIN_TOP,
    left: containerRect.left + (containerRect.width - width) / 2,
    width,
    height: Math.max(0, rowHeight - SOLO_CARD_MARGIN_TOP - SOLO_CARD_MARGIN_BOTTOM),
  };
}

// ── Module-open SPLIT placement geometry ─────────────────────────────────────
// In the split view the Brief docks into the chat-box grid's RIGHT column.
// Mirrors --chat-split-right-col: clamp(360px, 33vw, 540px) and the
// `grid-template-rows` + .chat-brief-pane margins in ChatBox.css. KEEP IN SYNC
// with those rules. The Brief is the TOP THIRD when it shares the column with an
// open chat (`1fr 2fr`); when the chat is DOCKED (closed) the grid is `1fr 0fr`
// and the Brief fills the WHOLE right column, so the open-flight target must be
// that full-height rect — otherwise the flight lands on the top third and the
// CSS grid then visibly expands it ("opens small, then grows").
const SPLIT_COL_MIN_PX = 360;
const SPLIT_COL_MAX_PX = 540;
const SPLIT_COL_VW_FRACTION = 0.33;
// .chat-brief-pane { margin: 16px 16px 8px 8px } (top right bottom left); the
// 8px bottom + the chat card's 8px top = the standard 16px Brief↔chat gap. When
// the chat is DOCKED (Brief fills the column) the bottom grows to 16px.
const SPLIT_PANE_MARGIN_TOP = 16;
const SPLIT_PANE_MARGIN_RIGHT = 16;
const SPLIT_PANE_MARGIN_BOTTOM = 8;
const SPLIT_PANE_MARGIN_BOTTOM_DOCKED = 16;
const SPLIT_PANE_MARGIN_LEFT = 8;

/**
 * Resting rect of the docked Brief in the module-open split — the right column
 * of the chat-box-root, inset by the .chat-brief-pane margins. Used as the OPEN
 * flight's landing target before the split pane mounts.
 *
 * `chatDocked` selects the row height: the top third (`1fr 2fr`) when the chat
 * shares the column, or the FULL column height (`1fr 0fr`, chat collapsed) when
 * the chat is docked — so the Brief opens directly to its larger resting size
 * instead of landing on the top third and expanding. KEEP IN SYNC with
 * `.chat-pane-collapsed:has(.chat-brief-pane)` in 09-split-pane.css.
 */
export function computeBriefSplitRect(
  rootRect: DockRect,
  viewportWidth: number,
  chatDocked = false,
): DockRect {
  const colWidth = Math.min(
    Math.max(SPLIT_COL_MIN_PX, viewportWidth * SPLIT_COL_VW_FRACTION),
    SPLIT_COL_MAX_PX,
    rootRect.width,
  );
  // Chat open → the Brief is the top row of `1fr 2fr` (one third). Chat docked →
  // the grid is `1fr 0fr`, so the Brief fills the full grid height. The bottom
  // margin is 8px when the chat shares the column (→ 16px gap to it) or 16px when
  // the Brief fills the column alone.
  const paneHeight = chatDocked ? rootRect.height : rootRect.height / 3;
  const bottomMargin = chatDocked ? SPLIT_PANE_MARGIN_BOTTOM_DOCKED : SPLIT_PANE_MARGIN_BOTTOM;
  return {
    top: rootRect.top + SPLIT_PANE_MARGIN_TOP,
    left: rootRect.left + rootRect.width - colWidth + SPLIT_PANE_MARGIN_LEFT,
    width: Math.max(0, colWidth - SPLIT_PANE_MARGIN_LEFT - SPLIT_PANE_MARGIN_RIGHT),
    height: Math.max(0, paneHeight - SPLIT_PANE_MARGIN_TOP - bottomMargin),
  };
}
